import { useMemo } from 'react';
import { Card } from '@/components/ui/Card';
import { cn } from '@/lib/utils';
import { getActionMeta } from '@shared/types/domino-actions';
import { getStepWarnings } from './step-warnings';
import type {
  ExecutionStep,
  ExecutionStepStatus,
  FlowExecutionStatus,
} from '@shared/types/flow-executions';

interface ExecutionTimelineRailProps {
  steps: ExecutionStep[];
  snapshotDominos?: Array<{
    position?: number;
    action_type?: string;
    label?: string;
  }>;
  executionStatus: FlowExecutionStatus;
  /** Kept for backwards-compatibility with the existing call sites; no longer influences rendering. */
  compact?: boolean;
  selectedStepId?: string | null;
  onStepSelect?: (step: ExecutionStep) => void;
}

interface StatusVisual {
  label: string;
}

const STATUS_VISUALS: Record<ExecutionStepStatus, StatusVisual> = {
  pending: {
    label: 'En attente',
  },
  running: {
    label: 'En cours',
  },
  waiting_hitl: {
    label: 'Validation requise',
  },
  completed: {
    label: 'Exécuté',
  },
  failed: {
    label: 'En erreur',
  },
  skipped: {
    label: 'Ignoré',
  },
};

const STATUS_PILL: Record<ExecutionStepStatus, string> = {
  pending: 'bg-gray-100 text-gray-600',
  running: 'bg-amber-50 text-amber-700',
  waiting_hitl: 'bg-orange-50 text-orange-700',
  completed: 'bg-emerald-50 text-emerald-700',
  failed: 'bg-red-50 text-red-700',
  skipped: 'bg-gray-100 text-gray-500',
};

const getEmptyMessage = (status: FlowExecutionStatus): string => {
  switch (status) {
    case 'pending':
    case 'running':
      return "Démarrage de l'exécution...";
    case 'paused':
      return 'Aucune étape active pour le moment.';
    case 'failed':
      return "Exécution terminée avec erreur avant production d'étapes exploitables.";
    default:
      return 'Aucune étape disponible.';
  }
};

const extractErrorMessage = (error: ExecutionStep['error']): string | null => {
  if (!error || typeof error !== 'object') return null;
  const message =
    (error as Record<string, unknown>).errorMessage ??
    (error as Record<string, unknown>).message;
  return typeof message === 'string' ? message : null;
};

/**
 * Resolve a user-facing label for a step. Priority:
 *  1. The domino's own label (set by the planner)
 *  2. The action's registry label (from DOMINO_ACTION_REGISTRY)
 *  3. The raw action_type as a last resort
 *
 * No JSON is ever surfaced here — the steps list is a status overview, not a debugger.
 */
const resolveStepLabel = (
  step: ExecutionStep,
  snapshotDominos: ExecutionTimelineRailProps['snapshotDominos']
): string => {
  const fromSnapshot =
    snapshotDominos?.find((d) => d.position === step.position)?.label ??
    snapshotDominos?.[step.position - 1]?.label;
  if (typeof fromSnapshot === 'string' && fromSnapshot.trim().length > 0) {
    return fromSnapshot.trim();
  }
  const meta = getActionMeta(step.action_type as never);
  if (meta?.label) return meta.label;
  return step.action_type;
};

export const ExecutionTimelineRail = ({
  steps,
  snapshotDominos,
  executionStatus,
  selectedStepId = null,
  onStepSelect,
}: ExecutionTimelineRailProps) => {
  const orderedSteps = useMemo(
    () => [...steps].sort((a, b) => a.position - b.position),
    [steps]
  );

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-3">
        <h2 className="text-sm font-semibold text-gray-900">Étapes</h2>
        <span className="text-xs text-gray-500">
          {orderedSteps.length} étape{orderedSteps.length > 1 ? 's' : ''}
        </span>
      </div>

      {orderedSteps.length === 0 ? (
        <Card className="py-6 text-center">
          {(executionStatus === 'pending' || executionStatus === 'running') && (
            <div className="mx-auto mb-3 h-5 w-5 animate-spin rounded-full border-2 border-gray-300 border-t-gray-900" />
          )}
          <p className="text-sm text-gray-500">{getEmptyMessage(executionStatus)}</p>
        </Card>
      ) : (
        <ul className="overflow-hidden rounded-xl border border-gray-200 bg-white">
          {orderedSteps.map((step, index) => {
            const visual = STATUS_VISUALS[step.status];
            const label = resolveStepLabel(step, snapshotDominos);
            const errorMessage =
              step.status === 'failed' ? extractErrorMessage(step.error) : null;
            const warningCount = getStepWarnings(step).length;
            return (
              <li
                key={step.id}
                className={cn(index > 0 ? 'border-t border-gray-100' : '')}
              >
                <button
                  type="button"
                  className={cn(
                    'flex w-full items-start gap-3 px-3 py-3 text-left transition-colors hover:bg-gray-50 focus:outline-none focus-visible:bg-gray-50 focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-domino-aubergine/30',
                    selectedStepId === step.id ? 'bg-domino-muted/70' : ''
                  )}
                  onClick={() => onStepSelect?.(step)}
                >
                  <span className="mt-0.5 w-5 shrink-0 text-[11px] font-medium text-gray-400">
                    {step.position}
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="break-words text-sm font-medium leading-5 text-gray-900">
                      {label}
                    </p>
                    {errorMessage ? (
                      <p className="mt-1 break-words text-xs leading-5 text-red-600">
                        {errorMessage}
                      </p>
                    ) : null}
                    {warningCount > 0 ? (
                      <p className="mt-1 text-xs leading-5 text-amber-700">
                        ⚠ {warningCount} avertissement{warningCount > 1 ? 's' : ''}
                      </p>
                    ) : null}
                  </div>
                  <span
                    className={cn(
                      'shrink-0 rounded-full px-2 py-0.5 text-[11px] font-medium',
                      STATUS_PILL[step.status]
                    )}
                  >
                    {visual.label}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};
